import React, { useEffect, useReducer } from 'react';
import {
  Button, Dropdown, IconButton, Message, Stack, Table, toaster,
} from 'rsuite';
import { v4 } from 'uuid';
import { AxiosError } from 'axios';
import _ from 'lodash';
import { BsPlusCircle } from 'react-icons/bs';
import { HiDotsHorizontal } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { Eshop } from '../../../api/eshop/eshop';
import {
  WarehouseLocality,
  WarehouseLocalityStatus,
  WarehouseSynchronizationResult,
} from '../../../api/warehouse/warehouseApiTypes';
import { getWarehouseLocalities, synchronizeWarehouseLocalities } from '../../../api/warehouse/warehouse';
import DropdownOverlay from '../../DropdownOverlay';
import { WAREHOUSE_LOCALITY_EDIT } from '../../../routes/links';

const STATUS_LABELS: Record<WarehouseLocalityStatus, string> = {
  [WarehouseLocalityStatus.SYNCHRONIZED]: 'Synchronized',
  [WarehouseLocalityStatus.NOT_SYNCHRONIZED]: 'Not synchronized',
  [WarehouseLocalityStatus.NOT_ON_MARKETPLACE]: 'Not on marketplace',
  [WarehouseLocalityStatus.NOT_IMPORTED]: 'Not imported',
};

interface MenuProps {
  onEditClick: () => void;
}

function Menu({ onEditClick }: MenuProps) {
  return (
    <DropdownOverlay
      element={(
        <IconButton
          size="sm"
          appearance="subtle"
          icon={<HiDotsHorizontal size={20} />}
        />
)}
    >
      <Dropdown.Menu>
        <Dropdown.Item
          onClick={() => onEditClick()}
        >
          Edit
        </Dropdown.Item>
      </Dropdown.Menu>
    </DropdownOverlay>
  );
}

interface Props {
  eshop: Eshop
}

interface State {
  isLoading: boolean;
  isSynchronizing: boolean;
  warehouses: WarehouseLocality[];
  error?: string
}

const initialState: State = {
  isLoading: false,
  isSynchronizing: false,
  warehouses: [],
};

type LoadingAction = { type: 'loading' };
type LoadingSuccessAction = { type: 'loadingSuccess', payload: WarehouseLocality[] };
type LoadingFailureAction = { type: 'loadingFailure', payload: string };
type SynchronizeAction = { type: 'synchronize' };
type SynchronizeSuccessAction = { type: 'synchronizeSuccess' };
type SynchronizeFailureAction = { type: 'synchronizeFailure', payload: string };
type Action = LoadingAction
| LoadingSuccessAction
| LoadingFailureAction
| SynchronizeAction
| SynchronizeSuccessAction
| SynchronizeFailureAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'loading':
      return { ...state, isLoading: true, warehouses: [] };
    case 'loadingSuccess':
      return { ...state, isLoading: false, warehouses: action.payload };
    case 'loadingFailure':
      return { ...state, isLoading: false, error: action.payload };
    case 'synchronize':
      return { ...state, isSynchronizing: true };
    case 'synchronizeSuccess':
      return { ...state, isSynchronizing: false };
    case 'synchronizeFailure':
      return { ...state, isSynchronizing: false, error: action.payload };
    default:
      return state;
  }
}

export default function EshopSetupKauflandWarehouses({ eshop }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();

  function showErrorMessage(message: string): void {
    toaster.push(<Message type="error" closable showIcon duration={2000}>{message}</Message>);
  }

  function showSuccessMessage() {
    toaster.push(<Message type="success" closable showIcon duration={2000}>Warehouses successfully synchronized.</Message>);
  }

  function showDependingMessage(synchronizationResult: WarehouseSynchronizationResult): void {
    if (synchronizationResult.notDeletedWarehouseLocalities.length === 0) {
      showSuccessMessage();
      return;
    }

    const { notDeletedWarehouseLocalities } = synchronizationResult;
    const messages = notDeletedWarehouseLocalities
      .map(notDeleted => `Cannot delete warehouse with id ${notDeleted.warehouseLocality.id}.
      It is used in ${notDeleted.numberOfAssignedProducts} products.`);

    toaster.push(
      <Message type="warning" closable showIcon duration={0}>
        {messages.map(message => (<p key={v4()}>{message}</p>))}
      </Message>,
    );
  }

  function fetchWarehouseLocalities(): Promise<WarehouseLocality[]> {
    return getWarehouseLocalities(eshop.code)
      .catch((error: AxiosError) => {
        if (error.response?.status !== 400) {
          return Promise.reject(error);
        }
        return [];
      });
  }

  function loadWarehouses(): void {
    dispatch({ type: 'loading' });
    fetchWarehouseLocalities()
      .then((warehouses) => dispatch({ type: 'loadingSuccess', payload: warehouses }))
      .catch((error: AxiosError) => {
        dispatch({ type: 'loadingFailure', payload: error.response?.data.details });
        showErrorMessage(error.response?.data.details);
      });
  }

  useEffect(() => {
    loadWarehouses();
  }, [eshop]);

  function synchronizeWarehouses(): void {
    dispatch({ type: 'synchronize' });
    synchronizeWarehouseLocalities(eshop.code)
      .then(response => {
        dispatch({ type: 'synchronizeSuccess' });
        showDependingMessage(response);
        loadWarehouses();
      })
      .catch((error: AxiosError) => {
        dispatch({ type: 'synchronizeFailure', payload: error.response?.data.details });
        showErrorMessage(error.response?.data.details);
      });
  }

  function handleEdit(id: number): void {
    navigate(`${WAREHOUSE_LOCALITY_EDIT}/${id}`);
  }

  function handleAdd(): void {
    navigate(WAREHOUSE_LOCALITY_EDIT);
  }

  return (
    <Stack spacing={15} direction="column" alignItems="stretch" justifyContent="flex-start">
      <Button
        appearance="primary"
        onClick={() => synchronizeWarehouses()}
        loading={state.isSynchronizing}
      >
        Synchronize warehouse data from Kaufland
      </Button>
      <Table
        autoHeight
        bordered
        data={state.warehouses}
        loading={state.isLoading}
      >
        <Table.Column flexGrow={1} align="center">
          <Table.HeaderCell>Warehouse Id</Table.HeaderCell>
          <Table.Cell dataKey="code" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.Cell dataKey="name" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>Street</Table.HeaderCell>
          <Table.Cell dataKey="street" />
        </Table.Column>
        <Table.Column flexGrow={1} align="center">
          <Table.HeaderCell>House number</Table.HeaderCell>
          <Table.Cell dataKey="houseNumber" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>City</Table.HeaderCell>
          <Table.Cell dataKey="city" />
        </Table.Column>
        <Table.Column flexGrow={1} align="center">
          <Table.HeaderCell>Postcode</Table.HeaderCell>
          <Table.Cell dataKey="postcode" />
        </Table.Column>
        <Table.Column flexGrow={1} align="center">
          <Table.HeaderCell>Country</Table.HeaderCell>
          <Table.Cell dataKey="countryCode" />
        </Table.Column>
        <Table.Column flexGrow={1} align="center">
          <Table.HeaderCell>Phone</Table.HeaderCell>
          <Table.Cell dataKey="phone" />
        </Table.Column>
        <Table.Column flexGrow={2} align="center">
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.Cell>
            {
              (warehouse: WarehouseLocality) => (
                warehouse.status ? STATUS_LABELS[warehouse.status] : null
              )
            }
          </Table.Cell>
        </Table.Column>
        <Table.Column flexGrow={1} align="center" verticalAlign="middle">
          <Table.HeaderCell>{}</Table.HeaderCell>
          <Table.Cell style={{ padding: '0px' }}>
            {({ id }: WarehouseLocality) => (
              _.isNil(id) ? null : <Menu onEditClick={() => handleEdit(id)} />
            )}
          </Table.Cell>
        </Table.Column>
        <Table.Column flexGrow={1} align="center">
          <Table.HeaderCell>{}</Table.HeaderCell>
          <Table.Cell>
            {
              (warehouse: WarehouseLocality) => (
                <p>
                  {warehouse.isDefault ? '(DEFAULT)' : null}
                </p>
              )
            }
          </Table.Cell>
        </Table.Column>
      </Table>
      <IconButton
        size="md"
        appearance="subtle"
        icon={<BsPlusCircle size={20} />}
        onClick={() => handleAdd()}
      />
    </Stack>
  );
}
